////
/// ============================================================================
/// COMPONENT / HMK / MiniCart
/// ============================================================================
///
/// @group components
////

// Simple sprintf implementation for string formatting
function sprintf(str, ...args) {
  let i = 0;
  return str.replace(/%[sd%]/g, (match) => {
    if (match === "%%") return "%";
    if (match === "%s") return String(args[i++] || "");
    if (match === "%d") return Number(args[i++] || 0);
    return match;
  });
}

// Helper function to get translated strings
function t(key, count = null) {
  if (typeof minicart_i18n === "undefined") return key;

  const translation = minicart_i18n[key];
  if (!translation) return key;

  // Handle plural forms
  if (
    typeof translation === "object" &&
    translation.singular &&
    translation.plural
  ) {
    return count === 1 ? translation.singular : translation.plural;
  }

  return translation;
}

class MiniCart {
  constructor() {
    this.cartLinks = document.querySelectorAll(".js-cart-link");
    this.addToCartButtons = document.querySelectorAll("button.hmk-add2cart");
    this.miniCart = document.querySelector(".js-mini-cart");
    this.tableWrapper = this.miniCart.querySelector(
      ".js-mini-cart-table-wrapper"
    );
    this.tableTbody = this.tableWrapper.querySelector("tbody");
    this.tableTotalItems = this.tableWrapper.querySelector(
      "span.c-mini-cart_total_items"
    );
    this.tableSubTotal = this.tableWrapper.querySelector(
      "span.c-mini-cart_subtotal"
    );
    this.closeButton = this.miniCart.querySelector(".js-mini-cart-close");
    this.overlay = document.querySelector(".js-mini-cart-overlay");
    this.imageColumnEnabled = null;

    this.bindEvents();
  }

  bindEvents() {
    this.cartLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        this.showMiniCart();
      });
    });

    this.addToCartButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.disableAddToCartButtons();
      });
    });

    this.closeButton.addEventListener("click", () => this.hideMiniCart());
    this.overlay.addEventListener("click", () => this.hideMiniCart());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.miniCart.classList.contains("open")) {
        this.hideMiniCart();
      }
    });

    this.tableWrapper.addEventListener("click", async (event) => {
      const removeButton = event.target.closest(".js-mini-cart-remove");

      if (removeButton) {
        this.removeItem(removeButton.dataset.cartItemId);
      }
    });

    document.addEventListener("minicartGotContent", (event) => {
      // Check if wholesale grouping is active
      if (event.detail.wholesale_active) {
        this.fillBrandGroupedCartModal(event.detail);
      } else {
        this.fillCartModal(event.detail);
      }
      this.showMiniCart();
      this.enableAddToCartButtons();
    });

    document.addEventListener("minicartRowRemoved", (event) => {
      // Check if wholesale grouping is active and response includes minicart data
      if (event.detail.wholesale_active && event.detail.minicart) {
        // Use brand-grouped cart modal for wholesale display
        this.fillBrandGroupedCartModal(event.detail);
      } else {
        // Use traditional row removal for standard display
        this.removeRow(event.detail.removedItemID);
        this.updateCounter(event.detail.count);
        this.updatePrice(event.detail.total);
      }
    });

    document.addEventListener("minicartApiError", (event) => {
      this.enableAddToCartButtons();
    });
  }

  fillCartModal(details) {
    const qtyLabel =
      this.tableWrapper.querySelector("th.c-mini-cart_qty").innerText;
    const priceLabel = this.tableWrapper.querySelector(
      "th.c-mini-cart_price"
    ).innerText;

    const warningElement = document.querySelector("div.c-mini-cart_warning");
    if (warningElement) {
      warningElement.remove();
    }

    const warning = details.update_notice || "";
    if (warning !== "") {
      this.tableWrapper.insertAdjacentHTML(
        "beforebegin",
        `<div class="c-mini-cart_warning woocommerce-error">${warning}</div>`
      );
    }

    this.tableTbody.innerHTML = Object.entries(details.minicart)
      .map(
        ([cartItemID, item]) => `
			<tr>
				<td class="c-mini-cart_action">
					<span class="c-mini-cart_remove-icon js-mini-cart-remove" data-cart-item-id="${cartItemID}">
						<svg role="img" focusable="false"><use xlink:href="#icon--delete"></use></svg>
					</span>
				</td>
				<td class="c-mini-cart_desc">${item.description}</td>
				<td class="c-mini-cart_qty"><span class="u-only-mobile">${qtyLabel}: </span>${item.quantity}</td>
				<td class="c-mini-cart_price"><span class="u-only-mobile">${priceLabel}: </span>${item.discounted_price}</td>
			</tr>
		`
      )
      .join("");

    this.tableTotalItems.innerHTML = `${details.count}`;
    this.tableSubTotal.innerHTML = `${details.total}`;
  }

  /**
   * Fill mini-cart with brand-grouped items and CODA discounts
   */
  fillBrandGroupedCartModal(details) {
    // Fill mini-cart with wholesale brand grouping

    const qtyLabel =
      this.tableWrapper.querySelector("th.c-mini-cart_qty").innerText;
    const priceLabel = this.tableWrapper.querySelector(
      "th.c-mini-cart_price"
    ).innerText;

    // Handle update warnings
    const warningElement = document.querySelector("div.c-mini-cart_warning");
    if (warningElement) {
      warningElement.remove();
    }

    const warning = details.update_notice || "";
    if (warning !== "") {
      this.tableWrapper.insertAdjacentHTML(
        "beforebegin",
        `<div class="c-mini-cart_warning woocommerce-error">${warning}</div>`
      );
    }

    // Build brand-grouped HTML
    let html = "";
    let brandCount = 0;
    const canRenderImages = this.canRenderImageColumn();

    Object.entries(details.minicart).forEach(([brandName, brandData]) => {
      // Brand header
      html += this.renderBrandHeader(brandName, canRenderImages);

      // Regular items
      if (brandData.items && brandData.items.length > 0) {
        brandData.items.forEach((item) => {
          html += this.renderCartItem(
            item,
            qtyLabel,
            priceLabel,
            canRenderImages
          );
        });
      }

      // CODA row
      if (brandData.has_coda && brandData.coda_data) {
        html += this.renderCodaRow(
          brandData.coda_data,
          brandName,
          canRenderImages
        );
      }

      // Cosmetic summary row (for brands without CODA)
      if (brandData.cosmetic_summary && !brandData.has_coda) {
        html += this.renderCosmeticSummaryRow(
          brandData.cosmetic_summary,
          brandName,
          canRenderImages
        );
      }

      // Brand separator (except for last brand)
      brandCount++;
      if (brandCount < Object.keys(details.minicart).length) {
        html += this.renderBrandSeparator(canRenderImages);
      }
    });

    this.tableTbody.innerHTML = html;
    this.tableTotalItems.innerHTML = `${details.count}`;
    this.tableSubTotal.innerHTML = `${details.total}`;
  }

  /**
   * Render brand header row
   */
  renderBrandHeader(brandName, canRenderImages) {
    const colspan = canRenderImages ? 5 : 4;
    return `
            <tr class="mini-cart-brand-header">
                <td colspan="${colspan}" class="c-mini-cart_brand-name">
                    <strong>${brandName}</strong>
                </td>
            </tr>
        `;
  }

  /**
   * Format cart item description with sale and gift wrap info
   */
  formatCartItemDescription(item) {
    let description = `<span class="hmk_cdescr">${item.base_description}</span>`;

    // Add sale information if item is on sale
    if (item.on_sale === 1) {
      const regularPrice = new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        minimumFractionDigits: 0,
      }).format(item.price);

      if (item.lang === "ja") {
        const discountText = sprintf(t("percent_discount"), item.sale_amount);
        const originalPriceText = sprintf(t("original_price"), regularPrice);
        description =
          `<span class="hmk_cdisc">${discountText}</span> - ` + description;
        description += `<br/><span class="hmk_cdisc">${originalPriceText}</span>`;
      } else {
        const saleText = sprintf(
          t("sale_off_original"),
          item.sale_amount,
          regularPrice
        );
        description += `<br/><span class="hmk_cdisc">${saleText}</span>`;
      }
    }

    // Add gift wrap information if applicable
    if (item.gift_wrap_cost && item.gift_wrap_cost !== "") {
      const giftWrapText = t("gift_wrap");
      description += ` <span class="c-mini-cart_gift">(${giftWrapText})</span>`;
    }

    // Add eligibility note if present
    if (item.eligibility_note && item.eligibility_note !== "") {
      description += `<br><small class="eligibility-note">${item.eligibility_note}</small>`;
    }

    if (item.labor_note && item.labor_note !== "") {
      description += `<br><small class="eligibility-note labor-exclusion">${item.labor_note}</small>`;
    }

    return description;
  }

  /**
   * Format price with currency
   */
  formatPrice(amount) {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
      minimumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Render individual cart item
   */
  renderCartItem(item, qtyLabel, priceLabel, canRenderImages) {
    const description = this.formatCartItemDescription(item);
    const lineTotalPrice = this.formatPrice(item.line_total);
    const imageCell = canRenderImages
      ? `<td class="c-mini-cart_image">${item.thumbnail_html || "&nbsp;"}</td>`
      : "";

    return `
            <tr>
                <td class="c-mini-cart_action">
                    <span class="c-mini-cart_remove-icon js-mini-cart-remove" data-cart-item-id="${item.cart_item_key}">
                        <svg role="img" focusable="false"><use xlink:href="#icon--delete"></use></svg>
                    </span>
                </td>
                ${imageCell}
                <td class="c-mini-cart_desc">${description}</td>
                <td class="c-mini-cart_qty">
                    <span class="u-only-mobile">${qtyLabel}: </span>
                    ${item.quantity}                </td>
                <td class="c-mini-cart_price">
                    <span class="u-only-mobile">${priceLabel}: </span>
                    ${lineTotalPrice}                </td>
            </tr>
        `;
  }

  /**
   * Render CODA discount row
   */
  renderCodaRow(codaData, brandName, canRenderImages) {
    // Render CODA discount row

    const isMinimumOrder = codaData.is_minimum_order;
    const rowClass = isMinimumOrder
      ? "mini-cart-brand-coda minimum-order-warning"
      : "mini-cart-brand-coda";

    const itemCountText = sprintf(
      t("item_singular", codaData.item_count),
      codaData.item_count
    );
    const subtotalFormatted = this.formatPrice(codaData.subtotal);
    const discountAmountFormatted = this.formatPrice(
      Math.abs(codaData.discount_amount)
    );
    const subtotalLabel = t("subtotal");

    let leftContent = "";
    let rightContent = "";

    if (isMinimumOrder) {
      const minimumFormatted = this.formatPrice(codaData.minimum_required);
      const shortfallFormatted = this.formatPrice(codaData.shortfall);
      const minimumOrderText = sprintf(
        t("minimum_order"),
        minimumFormatted,
        shortfallFormatted
      );

      leftContent = `<div class="brand-coda-summary"><span class="coda-items-count">${itemCountText}</span><span class="coda-separator"> • </span><span class="coda-subtotal">${subtotalLabel}: ${subtotalFormatted}</span><br><small>${minimumOrderText}</small></div>`;
      rightContent = `<div class="discount-line"><span class="discount-amount">-${discountAmountFormatted}</span></div>`;
    } else {
      let leftContentBase = `<div class="brand-coda-summary"><span class="coda-items-count">${itemCountText}</span><span class="coda-separator"> • </span><span class="coda-subtotal">${subtotalLabel}: ${subtotalFormatted}</span>`;

      // Add volume discount progress if available
      if (codaData.volume_progress) {
        const amountNeededFormatted = this.formatPrice(
          codaData.volume_progress.amount_needed
        );
        const progressText = sprintf(
          t("add_more_discount"),
          amountNeededFormatted,
          codaData.volume_progress.discount_percentage
        );
        leftContentBase += `<br><small class="volume-progress">${progressText}</small>`;
      }

      leftContent = leftContentBase + "</div>";

      // Add profile color class if available
      const profileColor = codaData.profile_color || "";
      const badgeClass = profileColor
        ? `discount-badge ${profileColor}`
        : "discount-badge";
      const percentOffText = t("percent_off");

      // Translate "Volume Discount" if needed, similar to PHP template logic
      const profileDisplayName =
        codaData.profile_name === "Volume Discount"
          ? t("volume_discount")
          : codaData.profile_name;

      rightContent = `<div class="discount-line"><div class="discount-left"><span class="${badgeClass}">${profileDisplayName}</span><span class="discount-desc">${codaData.discount_percentage}${percentOffText}</span></div><span class="discount-amount">-${discountAmountFormatted}</span></div>`;
    }

    const descColspanAttr = canRenderImages ? ' colspan="2"' : "";
    return `
            <tr class="${rowClass}">
                <td class="c-mini-cart_action">
                            </td>
                <td class="c-mini-cart_desc"${descColspanAttr}>${leftContent}</td>
                            
                        <td colspan="2" class="c-mini-cart_price">
                                    ${rightContent}                            </td>
            </tr>
        `;
  }

  /**
   * Render cosmetic summary row for brands without CODA
   */
  renderCosmeticSummaryRow(summary, brandName, canRenderImages) {
    // Render cosmetic summary row

    const hasWarning = summary.has_minimum_warning;
    const rowClass = hasWarning
      ? "mini-cart-brand-coda minimum-order-warning"
      : "mini-cart-brand-coda";

    const itemCountText = sprintf(
      t("item_singular", summary.item_count),
      summary.item_count
    );
    const subtotalFormatted = this.formatPrice(summary.subtotal);
    const subtotalLabel = t("subtotal");

    let leftContent = `<div class="brand-coda-summary"><span class="coda-items-count">${itemCountText}</span><span class="coda-separator"> • </span><span class="coda-subtotal">${subtotalLabel}: ${subtotalFormatted}</span>`;

    if (hasWarning) {
      const minimumFormatted = this.formatPrice(summary.minimum_required);
      const shortfallFormatted = this.formatPrice(summary.shortfall);
      const minimumOrderText = sprintf(
        t("minimum_order"),
        minimumFormatted,
        shortfallFormatted
      );
      leftContent += `<br><small>${minimumOrderText}</small>`;
    } else if (summary.volume_progress) {
      // Add volume discount progress if available and no minimum warning
      const amountNeededFormatted = this.formatPrice(
        summary.volume_progress.amount_needed
      );
      const progressText = sprintf(
        t("add_more_discount"),
        amountNeededFormatted,
        summary.volume_progress.discount_percentage
      );
      leftContent += `<br><small class="volume-progress">${progressText}</small>`;
    }

    leftContent += `</div>`;

    const descColspanAttr = canRenderImages ? ' colspan="2"' : "";
    return `
            <tr class="${rowClass}">
                <td class="c-mini-cart_action">
                            </td>
                <td class="c-mini-cart_desc"${descColspanAttr}>${leftContent}</td>
                            
                        <td colspan="2" class="c-mini-cart_price">
                                                            </td>
            </tr>
        `;
  }

  /**
   * Render brand separator row
   */
  renderBrandSeparator(canRenderImages) {
    const colspan = canRenderImages ? 5 : 4;
    return `
            <tr class="mini-cart-group-separator">
                <td colspan="${colspan}">&nbsp;</td>
            </tr>
        `;
  }

  detectImageColumn() {
    const headerRow = this.tableWrapper.querySelector("thead tr");
    if (!headerRow) {
      return false;
    }

    return Array.from(headerRow.children).some((cell) =>
      cell.classList.contains("c-mini-cart_image")
    );
  }

  canRenderImageColumn() {
    if (this.imageColumnEnabled === null) {
      this.imageColumnEnabled = this.detectImageColumn();
    }

    return this.imageColumnEnabled;
  }

  showMiniCart() {
    this.miniCart.classList.add("-open");
    this.overlay.classList.add("-open");
    document.body.classList.add("mini-cart-open");
  }

  hideMiniCart() {
    this.miniCart.classList.remove("-open");
    this.overlay.classList.remove("-open");
    document.body.classList.remove("mini-cart-open");
  }

  disableAddToCartButtons() {
    this.addToCartButtons.forEach((button) => {
      button.disabled = true;
      button.classList.add("-loading");
      button.originalContent = button.innerHTML;
      button.innerHTML = '<span class="c-spinner -light"></span>';
    });
  }

  enableAddToCartButtons() {
    this.addToCartButtons.forEach((button) => {
      if (button.originalContent) {
        button.disabled = false;
        button.classList.remove("-loading");
        button.innerHTML = button.originalContent;
        delete button.originalContent;
      }
    });
  }

  removeItem(cartItemID) {
    const row = this.tableWrapper
      .querySelector(`.js-mini-cart-remove[data-cart-item-id="${cartItemID}"]`)
      .closest("tr");

    row.querySelector(".js-mini-cart-remove").innerHTML =
      '<span class="c-spinner -small"></span>';

    const minicartEvent = new CustomEvent("minicartRemoveRow", {
      detail: cartItemID,
    });
    document.dispatchEvent(minicartEvent);
  }

  removeRow(removedItemID) {
    const row = this.tableWrapper
      .querySelector(
        `.js-mini-cart-remove[data-cart-item-id="${removedItemID ?? ""}"]`
      )
      .closest("tr");
    row.remove();
  }

  updateCounter(count) {
    const countElement = this.tableWrapper.querySelector(
      "span.c-mini-cart_total_items"
    );
    countElement.innerHTML = count;
  }

  updatePrice(total) {
    const totalElement = this.tableWrapper.querySelector(
      "span.c-mini-cart_subtotal"
    );
    totalElement.innerHTML = total;
  }
}

export default () => {
  new MiniCart();
};
